import type { SupervisorFinding } from '@avisoma/shared';
import clsx from 'clsx';
import { useState } from 'react';
import { Badge } from '../../catalyst/badge';
import { Button } from '../../catalyst/button';
import { Label } from '../../catalyst/fieldset';
import { Switch, SwitchField } from '../../catalyst/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../catalyst/table';
import { Link } from '../../components/Link';
import { formatDateTime } from '../../lib/dateFormat';
import { COMPACT_BUTTON_CLASS } from '../../lib/findingActions';
import { FINDING_SEVERITY_COLOR, FINDING_STATUS_COLOR } from '../../lib/findingStatusColor';
import { presentSupervisorRun } from '../../lib/supervisorRunPresentation';
import { useAuth } from '../../hooks/useAuth';
import { emptyFindingsMessage, readShowAll, visibleFindings, writeShowAll } from './correctionsFilter';
import { formatValue, groupByCheck, latestPrecisionByCheck, pendingFinalizeLabel, staleObservedText, targetOf } from './correctionsGrouping';
import { FindingActions } from './FindingActions';
import { PrecisionChip } from './PrecisionChip';
import { AuditedChip } from './AuditedChip';
import { TabEmptyState } from './TabEmptyState';
import { useFinalizeBusy, useFinalizeProject } from './useFinalizeProject';
import { useSupervisorFindingMutations, type SupervisorFindingMutations } from './useSupervisorFindingMutations';
import { useSupervisorFindings } from './useSupervisorFindings';
import { useSupervisorPrecision } from './useSupervisorPrecision';

/**
 * Fixed-layout columns: every column but Reason has an explicit width, so Reason takes the
 * remainder and every per-check table lines up. Secondary metadata stacks inside two columns.
 */
const COLUMNS: Array<{ label: string; className?: string }> = [
  { label: 'Severity', className: 'w-19' },
  { label: 'Field', className: 'w-30' },
  { label: 'Old', className: 'w-28' },
  { label: 'Proposed', className: 'w-28' },
  { label: 'Reason' },
  { label: 'Status / Conf.', className: 'w-24' },
  { label: 'Model / Actor / Raised', className: 'w-36' },
  { label: 'Target', className: 'w-13' },
  { label: 'Actions', className: 'w-44' },
];
const MUTED = 'text-zinc-500 dark:text-zinc-400';

/** One line clipped with an ellipsis; the full text sits in the native tooltip. */
function Line({ text, className }: { text: string; className?: string }) {
  return (
    <div className={clsx('truncate', className)} title={text === '—' ? undefined : text}>
      {text}
    </div>
  );
}

function FindingRow({ f, actions }: { f: SupervisorFinding; actions: SupervisorFindingMutations | null }) {
  const target = targetOf(f);
  const stale = staleObservedText(f);
  return (
    <TableRow>
      <TableCell>
        <Badge color={FINDING_SEVERITY_COLOR[f.severity]}>{f.severity}</Badge>
      </TableCell>
      <TableCell className="font-mono text-xs">
        <Line text={`${f.family ? `${f.family}.` : ''}${f.field}`} />
      </TableCell>
      <TableCell className="font-mono text-xs">
        <Line text={formatValue(f.oldValue)} />
      </TableCell>
      <TableCell className="font-mono text-xs">
        {f.hasProposal ? (
          <Line text={formatValue(f.newValue)} />
        ) : (
          <span className="text-gray-400 dark:text-zinc-500">no proposal</span>
        )}
      </TableCell>
      <TableCell className="whitespace-normal break-words text-xs">
        {f.reason}
        {stale && <div className="mt-1 font-mono text-red-700 dark:text-red-400">{stale}</div>}
      </TableCell>
      <TableCell>
        <Badge color={FINDING_STATUS_COLOR[f.status]}>{f.status}</Badge>
        <div className={clsx('text-xs', MUTED)}>{f.confidence !== null ? f.confidence.toFixed(2) : '—'}</div>
      </TableCell>
      <TableCell className="text-xs">
        <Line text={f.model ?? '—'} />
        <Line text={f.actor} className={MUTED} />
        <Line text={formatDateTime(f.createdAt)} className={MUTED} />
      </TableCell>
      <TableCell className="text-xs">
        <Link to={target.href}>{target.label}</Link>
      </TableCell>
      <TableCell>{actions && <FindingActions finding={f} actions={actions} />}</TableCell>
    </TableRow>
  );
}

/** The project's supervisor findings, one table per check; admins get per-row actions. */
export function CorrectionsTab({ projectId }: { projectId: string }) {
  const { data, isLoading, isError } = useSupervisorFindings(projectId);
  const { hasAccess } = useAuth();
  const mutations = useSupervisorFindingMutations(projectId);
  // Admins only (spec "Decisions"): other roles see the read-only table.
  const actions = hasAccess('admin') ? mutations : null;
  const finalize = useFinalizeProject(projectId);
  // Busy through the queued handler too, not only the request: a second click would queue another finalize.
  const finalizeBusy = useFinalizeBusy(projectId, data?.finalizedAt);
  // Every role: the precision route uses requireAuth() only, so neither hook nor chip is admin-gated.
  const precision = useSupervisorPrecision();
  const [showAll, setShowAll] = useState(readShowAll);
  const toggleShowAll = (value: boolean) => {
    setShowAll(value);
    writeShowAll(value);
  };

  if (isLoading) return <TabEmptyState message="Loading supervisor findings…" />;
  if (isError || !data) {
    return (
      <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 shadow dark:shadow-zinc-950/30 rounded-lg p-6">
        <p className="text-center text-red-600 dark:text-red-400">Could not load supervisor findings.</p>
      </div>
    );
  }

  const run = presentSupervisorRun(data.run);
  const visible = visibleFindings(data.items, showAll);
  const groups = groupByCheck(visible);
  const empty = emptyFindingsMessage(data.items.length, visible.length);
  const precisionByCheck = latestPrecisionByCheck(precision.data?.items ?? []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Last run: {[run.label, run.timings, run.detail].filter(Boolean).join(' · ')}
        </p>
        <div className="flex flex-wrap items-center gap-4">
          {actions && data.pendingFinalizeCount > 0 && (
            <span className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
              {pendingFinalizeLabel(data.pendingFinalizeCount)}
              <Button
                outline
                buttonClassName={COMPACT_BUTTON_CLASS}
                disabled={finalizeBusy}
                onClick={() => finalize.mutate()}
              >
                {finalizeBusy ? 'Finalizing…' : 'Finalize'}
              </Button>
            </span>
          )}
          <SwitchField className="shrink-0">
            <Label className="text-sm whitespace-nowrap">Show all</Label>
            <Switch checked={showAll} onChange={toggleShowAll} aria-label="Show all findings" />
          </SwitchField>
        </div>
      </div>
      {empty && <TabEmptyState message={empty} />}
      {groups.map((g) => (
        <section
          key={g.checkId}
          className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-zinc-700">
            <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">
              {g.checkId} <span className="font-normal text-gray-500 dark:text-zinc-400">({g.items.length})</span>
            </h2>
            <span className="inline-flex items-center gap-1.5">
              <PrecisionChip precision={precisionByCheck.get(g.checkId)} weakLabelNote={precision.data?.weakLabelNote ?? ''} />
              <AuditedChip audited={precisionByCheck.get(g.checkId)?.audited} />
            </span>
          </div>
          <Table
            dense
            className="px-2 [&_table]:table-fixed [&_table]:w-full [&_table]:min-w-302 [&_td]:px-2! [&_th]:px-2! [&_th]:whitespace-normal [&>div]:w-full"
          >
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableHeader key={c.label} className={c.className}>
                    {c.label}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {g.items.map((f) => (
                <FindingRow key={f.id} f={f} actions={actions} />
              ))}
            </TableBody>
          </Table>
        </section>
      ))}
    </div>
  );
}
